import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Translates human-readable strings that the backend produces (notification
 * titles/bodies, `success_message` / `error_message` payloads).
 *
 * The backend is not localized — it emits English and wraps the dynamic part of
 * a sentence in square brackets, e.g. `Welcome [Ana Popescu]!`. This mirrors the
 * legacy Angular app (`modules/i18n/translation.service.ts` +
 * `notifications-inner.component.ts`): the English sentence itself is the
 * translation key, the bracketed value is lifted out into a `{{name}}`
 * parameter, and an unknown key falls back to the original English text so a
 * missing entry degrades to today's behaviour instead of showing a raw key.
 */
@Injectable({ providedIn: 'root' })
export class BackendI18nService {
  private readonly translate = inject(TranslateService);

  /**
   * Looks `key` up under `type` (the `ALERT` bucket by default, matching the
   * legacy service). Keys that already look like `SECTION.KEY` are treated as
   * full paths; plain English sentences are prefixed with `type`.
   */
  t(key?: string, params?: Record<string, unknown>, type = 'ALERT'): string {
    if (!key) return '';

    // Only treat the key as a full path when it looks like SectionName.KeyName.
    // Sentences such as 'Quiz not found.' also contain dots but must still be
    // prefixed with the section type.
    const isFullPath = /^[A-Za-z][A-Za-z0-9_]*\.[A-Za-z_]/.test(key);
    const fullKey = isFullPath ? key : `${type}.${key}`;

    const value = params
      ? this.translate.instant(fullKey, params)
      : this.translate.instant(fullKey);

    return value === fullKey ? key : value;
  }

  /**
   * Translates a backend notification title or body, lifting `[bracketed]`
   * values out into interpolation parameters.
   *
   * `Welcome [Ana]!` -> key `Welcome !` + `{ name: 'Ana' }` -> `Bun venit, Ana!`
   *
   * The legacy implementation stripped only the *first* bracket group, so a
   * message carrying two of them (the quiz reminder sends both a quiz name and
   * a minute count) never matched a key and fell back to raw English. Every
   * group is stripped here and exposed as `{{name}}`, `{{name2}}`, `{{name3}}`…
   * Single-bracket messages produce exactly the same key as before, so the
   * ported vocabulary keeps working unchanged.
   */
  translateMessage(msg?: string): string {
    return this.translateBracketed(msg, 'NOTIFICATION');
  }

  /**
   * Translates backend `error_message` / `success_message` strings under ALERT,
   * including bracketed dynamic values (e.g. assessment open time).
   */
  translateApiMessage(msg?: string): string {
    return this.translateBracketed(msg, 'ALERT');
  }

  private translateBracketed(msg: string | undefined, type: string): string {
    if (!msg) return '';

    const groups = [...msg.matchAll(/\[(.*?)\]/g)].map((m) => m[1]);
    if (!groups.length) {
      return this.t(msg, {}, type);
    }

    const textToTranslate = msg.replace(/\[(.*?)\]/g, '');
    const params: Record<string, string> = {};
    groups.forEach((value, i) => {
      params[i === 0 ? 'name' : `name${i + 1}`] = this.formatBracketValue(value);
    });

    return this.t(textToTranslate, params, type);
  }

  /** Localize ISO timestamps that the backend puts in brackets; leave other values as-is. */
  private formatBracketValue(value: string): string {
    if (!/^\d{4}-\d{2}-\d{2}T/.test(value)) return value;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }
}
